'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Session, RotatingWeek } from '@/types';
import LoginModal from '@/app/components/LoginModal';
import {
  ChevronDown, MapPin, Clock, Gauge, Heart, Target, Users, CheckCircle2,
  AlertTriangle, XCircle, Navigation, User, Info, Loader2, RefreshCw,
} from 'lucide-react';
import {
  fetchWeeklySessions,
  joinSession,
  leaveSession,
  getUserAllSessionStatuses,
  LiveSession,
  invalidateCache,
} from '@/lib/supabase/sessions';

const LOAD_TIMEOUT_MS = 9000; // 9 seconds — direct table queries are faster than RPC

function intensityBadgeClass(intensity: string) {
  if (intensity === 'Low') return 'intensity-badge-easy';
  if (intensity === 'Low-Mid') return 'intensity-badge-easy';
  if (intensity === 'Mid') return 'intensity-badge-mid';
  if (intensity === 'High') return 'intensity-badge-high';
  return 'intensity-badge-recovery';
}

function intensityLabel(intensity: string, t: (a: string, b: string) => string) {
  if (intensity === 'Low') return t('Low', '低强度');
  if (intensity === 'Low-Mid') return t('Low–Mid', '低中强度');
  if (intensity === 'Mid') return t('Mid', '中强度');
  if (intensity === 'High') return t('High', '高强度');
  return t('Recovery', '恢复');
}

function statusBadge(status: string, t: (a: string, b: string) => string) {
  const map: Record<string, { cls: string; icon: React.ReactNode; en: string; zh: string }> = {
    confirmed: { cls: 'status-badge-confirmed', icon: <CheckCircle2 size={11} />, en: 'Confirmed', zh: '已确认' },
    Confirmed: { cls: 'status-badge-confirmed', icon: <CheckCircle2 size={11} />, en: 'Confirmed', zh: '已确认' },
    planned: { cls: 'status-badge-planned', icon: <Info size={11} />, en: 'Planned', zh: '待安排' },
    Planned: { cls: 'status-badge-planned', icon: <Info size={11} />, en: 'Planned', zh: '待安排' },
    Changed: { cls: 'status-badge-changed', icon: <AlertTriangle size={11} />, en: 'Changed', zh: '已变更' },
    cancelled: { cls: 'status-badge-cancelled', icon: <XCircle size={11} />, en: 'Cancelled', zh: '已取消' },
    Cancelled: { cls: 'status-badge-cancelled', icon: <XCircle size={11} />, en: 'Cancelled', zh: '已取消' },
    draft: { cls: 'bg-gray-100 text-gray-600', icon: <Info size={11} />, en: 'Draft', zh: '草稿' },
    Draft: { cls: 'bg-gray-100 text-gray-600', icon: <Info size={11} />, en: 'Draft', zh: '草稿' },
  };
  const s = map[status] || map['planned'];
  return (
    <span className={`inline-flex items-center gap-1 text-card-label px-2 py-0.5 rounded-full ${s.cls}`}>
      {s.icon}
      {t(s.en, s.zh)}
    </span>
  );
}

function RotatingWeekCard({ week, t }: { week: RotatingWeek; t: (a: string, b: string) => string }) {
  return (
    <div className="bg-secondary/40 rounded-xl border border-border p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-card-label px-2 py-0.5 rounded-full bg-foreground text-primary-foreground">
          {t(week.weekLabel, week.weekLabelZH)}
        </span>
        <span className={`text-card-label px-2 py-0.5 rounded-full ${intensityBadgeClass(week.intensity)}`}>
          {intensityLabel(week.intensity, t)}
        </span>
      </div>
      <h4 className="text-sm font-bold text-foreground mb-1">{t(week.name, week.nameZH)}</h4>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mb-3">
        <span className="flex items-center gap-1"><MapPin size={11} />{t(week.location, week.locationZH)}</span>
        {week.distance && <span className="flex items-center gap-1"><Gauge size={11} />{week.distance}</span>}
        {week.duration && <span className="flex items-center gap-1"><Clock size={11} />{week.duration}</span>}
        {week.options && <span className="flex items-center gap-1"><Navigation size={11} />{t(week.options, week.optionsZH || week.options)}</span>}
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">
        <span className="font-semibold text-foreground">{t('Goal:', '目标：')}</span> {t(week.goal, week.goalZH)}
      </p>
      <p className="text-xs text-muted-foreground leading-relaxed mt-1">
        <span className="font-semibold text-foreground">{t('Notes:', '备注：')}</span> {t(week.notes, week.notesZH)}
      </p>
    </div>
  );
}

// Convert LiveSession to Session shape for display — bilingual fields switch by language
function liveToSession(live: LiveSession): Session {
  const dayMap: Record<string, string> = {
    Monday: '周一', Tuesday: '周二', Wednesday: '周三', Thursday: '周四',
    Friday: '周五', Saturday: '周六', Sunday: '周日',
  };
  return {
    id: live.id,
    day: live.day_of_week as any,
    dayZH: dayMap[live.day_of_week] || live.day_of_week,
    name: live.session_name_en || live.day_of_week,
    nameZH: live.session_name_zh || live.session_name_en || '',
    time: live.time_slot || '',
    location: live.location || '',
    locationZH: live.location_zh || live.location || '',
    distance: live.distance || undefined,
    duration: live.duration || undefined,
    pace: live.pace || undefined,
    hrZone: live.hr_zone || undefined,
    intensity: (live.intensity as any) || 'Low',
    goal: live.goal_en || '',
    goalZH: live.goal_zh || live.goal_en || '',
    notes: live.notes_en || '',
    notesZH: live.notes_zh || live.notes_en || '',
    suitableFor: live.suitable_for_en || '',
    suitableForZH: live.suitable_for_zh || live.suitable_for_en || '',
    tags: live.tags || [],
    participants: live.participants_count || 0,
    capacity: live.capacity || 25,
    status: ((live.status || 'planned').toLowerCase() as any),
    host: live.host_name || undefined,
    meetingPoint: live.meeting_point || undefined,
    meetingPointZH: live.meeting_point_zh || live.meeting_point || undefined,
    routeLink: live.route_link || undefined,
  };
}

// ─── Your Joined Sessions Summary ────────────────────────────────────────────
function JoinedSessionsSummary({
  sessions,
  userStatuses,
  participantCounts,
  onLeave,
  leavingId,
  t,
}: {
  sessions: Session[];
  userStatuses: Record<string, string>;
  participantCounts: Record<string, number>;
  onLeave: (sessionId: string) => void;
  leavingId: string | null;
  t: (a: string, b: string) => string;
}) {
  const { language } = useLanguage();
  const isZH = language === 'ZH';

  const joinedSessions = sessions.filter((s) => {
    const status = userStatuses[s.id];
    return status === 'joined' || status === 'present' || status === 'late';
  });

  // Sort joined sessions MON–SUN, then by time ascending
  const DAY_ORDER_MAP: Record<string, number> = {
    Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3, Friday: 4, Saturday: 5, Sunday: 6,
  };

  function parseTimeToMinutes(time: string): number {
    if (!time) return 9999;
    const match = time.match(/(\d{1,2}):(\d{2})/);
    if (!match) return 9999;
    return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
  }

  const sortedJoined = [...joinedSessions].sort((a, b) => {
    const dayA = DAY_ORDER_MAP[a.day] ?? 7;
    const dayB = DAY_ORDER_MAP[b.day] ?? 7;
    if (dayA !== dayB) return dayA - dayB;
    return parseTimeToMinutes(a.time) - parseTimeToMinutes(b.time);
  });

  // Today's Training Priority Logic
  const todayDayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const now = new Date();
  const todayDayName = todayDayNames[now.getDay()];
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const SESSION_GRACE_MINUTES = 90;

  // Find today's active session first
  const todaySession = sortedJoined.find((s) => {
    if (s.day !== todayDayName) return false;
    const startMinutes = parseTimeToMinutes(s.time);
    return startMinutes === 9999 || currentMinutes < startMinutes + SESSION_GRACE_MINUTES;
  });

  // If no today session, find next future session
  const nextSession = todaySession || sortedJoined.find((s) => {
    const dayIdx = DAY_ORDER_MAP[s.day] ?? 7;
    const todayIdx = DAY_ORDER_MAP[todayDayName] ?? 7;
    if (dayIdx > todayIdx) return true;
    if (dayIdx === todayIdx) {
      const startMinutes = parseTimeToMinutes(s.time);
      return startMinutes === 9999 || currentMinutes < startMinutes + SESSION_GRACE_MINUTES;
    }
    return false;
  }) || sortedJoined[0] || null;

  const isNextSessionToday = nextSession?.day === todayDayName;
  const nextSessionLabel = isNextSessionToday ? t("TODAY'S TRAINING", '今日训练') : t('NEXT SESSION', '下次训练');

  return (
    <div className="mb-6 max-w-4xl">
      {/* My Week summary widget */}
      <div style={{ border: '1px solid #D8D8CE', borderRadius: 2, backgroundColor: '#FFFFFF', overflow: 'hidden' }}>
        {/* Header row */}
        <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #D8D8CE', backgroundColor: '#F7F7F2' }}>
          <CheckCircle2 size={13} style={{ color: '#00C26E', flexShrink: 0 }} />
          <h3 style={{ fontSize: 10, fontWeight: 700, color: '#070707', textTransform: 'uppercase', letterSpacing: '0.12em', flex: 1, fontFamily: 'var(--font-mono, monospace)', margin: 0 }}>
            {t('MY WEEK', '我的本周')}
          </h3>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#070707', backgroundColor: '#B8FF00', border: '1px solid #B8FF00', padding: '2px 8px', borderRadius: 2, fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {t('Sessions Joined', '已加入训练')}: {sortedJoined.length}
          </span>
        </div>

        {sortedJoined.length === 0 ? (
          <div style={{ padding: '16px', fontSize: 12, color: '#5F625F', textAlign: 'center', fontFamily: 'var(--font-body, sans-serif)' }}>
            {t('No sessions joined this week. Browse the schedule below to join.', '本周尚未加入任何训练，请在下方日程中加入。')}
          </div>
        ) : (
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Next/Today session compact highlight */}
            {nextSession && (
              <div>
                <p style={{ fontSize: 9, fontWeight: 700, color: '#5F625F', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 6, fontFamily: 'var(--font-mono, monospace)' }}>
                  {nextSessionLabel}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, backgroundColor: '#EDFFD0', borderRadius: 2, padding: '10px 12px', border: '1px solid #B8FF00' }}>
                  {/* Day pill */}
                  <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 2, backgroundColor: '#B8FF00', border: '1px solid #A4E600', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <p style={{ fontSize: 9, fontWeight: 900, color: '#070707', textTransform: 'uppercase', lineHeight: 1, fontFamily: 'var(--font-mono, monospace)' }}>
                      {isZH ? nextSession.dayZH : nextSession.day.slice(0, 3)}
                    </p>
                    {nextSession.time && (
                      <p style={{ fontSize: 9, fontWeight: 700, color: '#070707', lineHeight: 1, marginTop: 2, fontFamily: 'var(--font-mono, monospace)' }}>
                        {nextSession.time.split(':').slice(0, 2).join(':')}
                      </p>
                    )}
                  </div>
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#070707', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', textTransform: 'uppercase' }}>
                      {isZH ? (nextSession.nameZH || nextSession.name) : nextSession.name}
                    </p>
                    {(isZH ? nextSession.locationZH : nextSession.location) && (
                      <p style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#5F625F', marginTop: 2, fontFamily: 'var(--font-body, sans-serif)' }}>
                        <MapPin size={9} />
                        {(isZH ? nextSession.locationZH : nextSession.location)?.split(',')[0]}
                      </p>
                    )}
                  </div>
                  {/* Quick leave */}
                  <button
                    onClick={() => onLeave(nextSession.id)}
                    disabled={leavingId === nextSession.id}
                    style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, padding: '6px 10px', borderRadius: 2, border: '1.5px solid #FF3B1F', backgroundColor: 'transparent', color: '#FF3B1F', cursor: 'pointer', fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.06em', textTransform: 'uppercase', opacity: leavingId === nextSession.id ? 0.5 : 1 }}
                    title={t('Leave session', '退出训练')}
                  >
                    {leavingId === nextSession.id ? <Loader2 size={10} className="animate-spin" /> : <XCircle size={10} />}
                    {t('Leave', '退出')}
                  </button>
                </div>
              </div>
            )}

            {/* Other joined sessions */}
            {sortedJoined.length > 1 && (
              <div>
                <p style={{ fontSize: 9, fontWeight: 700, color: '#5F625F', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 6, fontFamily: 'var(--font-mono, monospace)' }}>
                  {t('ALSO JOINED', '其他已加入')}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {sortedJoined
                    .filter((s) => s.id !== nextSession?.id)
                    .map((session) => {
                      const sessionName = isZH ? (session.nameZH || session.name) : session.name;
                      const dayLabel = isZH ? session.dayZH : session.day.slice(0, 3);
                      return (
                        <div key={session.id} style={{ display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#F7F7F2', borderRadius: 2, padding: '8px 12px', border: '1px solid #D8D8CE' }}>
                          <span style={{ fontSize: 10, fontWeight: 900, color: '#070707', textTransform: 'uppercase', width: 32, flexShrink: 0, fontFamily: 'var(--font-mono, monospace)' }}>{dayLabel}</span>
                          {session.time && <span style={{ fontSize: 10, color: '#5F625F', flexShrink: 0, fontFamily: 'var(--font-mono, monospace)' }}>{session.time}</span>}
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#070707', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-body, sans-serif)' }}>{sessionName}</span>
                          <button
                            onClick={() => onLeave(session.id)}
                            disabled={leavingId === session.id}
                            style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, padding: '4px 8px', borderRadius: 2, border: '1.5px solid #FF3B1F', backgroundColor: 'transparent', color: '#FF3B1F', cursor: 'pointer', fontFamily: 'var(--font-mono, monospace)', opacity: leavingId === session.id ? 0.5 : 1 }}
                          >
                            {leavingId === session.id ? <Loader2 size={9} className="animate-spin" /> : <XCircle size={9} />}
                            {t('Leave', '退出')}
                          </button>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SessionAccordion({
  session,
  isExpanded,
  onToggle,
  participationStatus,
  onJoin,
  onLeave,
  participantCount,
  waitlistCount,
  joining,
}: {
  session: Session;
  isExpanded: boolean;
  onToggle: () => void;
  participationStatus: string | null;
  onJoin: () => void;
  onLeave: () => void;
  participantCount: number;
  waitlistCount: number;
  joining: boolean;
}) {
  const { t, language } = useLanguage();
  const [activeRotatingWeek, setActiveRotatingWeek] = useState(0);

  const isCancelled = session.status === 'Cancelled' || session.status === 'cancelled';
  const isFull = participantCount >= session.capacity;
  const isJoined = participationStatus === 'joined' || participationStatus === 'present' || participationStatus === 'late';
  const isWaitlisted = participationStatus === 'waitlist';
  const spotsLeft = Math.max(0, session.capacity - participantCount);
  const fillPct = Math.min((participantCount / session.capacity) * 100, 100);

  const isZH = language === 'ZH';
  const sessionName = isZH ? (session.nameZH || session.name) : session.name;
  const sessionLocation = isZH ? (session.locationZH || session.location) : session.location;
  const sessionGoal = isZH ? (session.goalZH || session.goal) : session.goal;
  const sessionNotes = isZH ? (session.notesZH || session.notes) : session.notes;
  const sessionSuitableFor = isZH ? (session.suitableForZH || session.suitableFor) : session.suitableFor;
  const sessionMeetingPoint = isZH ? (session.meetingPointZH || session.meetingPoint) : session.meetingPoint;
  // Day label: larger, stronger contrast
  const sessionDayLabel = isZH ? session.dayZH : session.day.slice(0, 3).toUpperCase();

  return (
    <div
      className={`overflow-hidden transition-all duration-200`}
      style={{
        borderRadius: 2,
        border: isCancelled ? '1px solid #FFBFB8' : isJoined ? '2px solid #B8FF00' : '1px solid #D8D8CE',
        backgroundColor: '#FFFFFF',
        opacity: isCancelled ? 0.75 : 1,
        borderLeft: isJoined ? '4px solid #B8FF00' : undefined,
      }}
    >
      {/* ── Collapsed header — tap to expand ── */}
      <button
        onClick={onToggle}
        className={`w-full text-left transition-colors ${
          isJoined ? 'hover:bg-green-50/50' : 'hover:bg-secondary/30'
        }`}
        aria-expanded={isExpanded}
      >
        {/* Mobile layout */}
        <div className="sm:hidden px-4 py-4">
          {/* Row 1: Day label (large, strong) + Joined badge */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-base font-black text-foreground tracking-widest uppercase leading-none">{sessionDayLabel}</span>
              {statusBadge(session.status, t)}
            </div>
            <div className="flex items-center gap-2">
              {isJoined && (
                <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-green-100 text-green-700 border border-green-200">
                  <CheckCircle2 size={11} />
                  {t('✓ Joined', '已加入')}
                </span>
              )}
              {isWaitlisted && (
                <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                  <AlertTriangle size={11} />
                  {t('Waitlist', '候补')}
                </span>
              )}
              <ChevronDown
                size={16}
                className={`text-muted-foreground transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
              />
            </div>
          </div>
          {/* Row 2: Session name — larger, bold */}
          <h3 className="text-[18px] font-black text-foreground leading-tight mb-2">{sessionName}</h3>
          {/* Row 3: Key info chips — all visible without expanding */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {session.time && (
              <span className="flex items-center gap-1 text-sm font-bold text-foreground">
                <Clock size={13} className="text-muted-foreground" />{session.time}
              </span>
            )}
            {sessionLocation && (
              <span className="flex items-center gap-1 text-sm font-semibold text-foreground">
                <MapPin size={13} className="text-muted-foreground" />{sessionLocation.split(',')[0]}
              </span>
            )}
            {session.distance && (
              <span className="flex items-center gap-1 text-sm font-bold text-foreground">
                <Gauge size={13} className="text-muted-foreground" />{session.distance}
              </span>
            )}
            {!session.distance && session.duration && (
              <span className="flex items-center gap-1 text-sm font-bold text-foreground">
                <Clock size={13} className="text-muted-foreground" />{session.duration}
              </span>
            )}
            <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${intensityBadgeClass(session.intensity)}`}>
              {intensityLabel(session.intensity, t)}
            </span>
            {!isJoined && !isWaitlisted && !isCancelled && (
              <span className={`text-xs font-semibold ${isFull ? 'text-red-500' : 'text-muted-foreground'}`}>
                {isFull ? t('Full', '已满') : `${spotsLeft} ${t('spots left', '名额剩余')}`}
              </span>
            )}
          </div>
        </div>

        {/* Desktop layout */}
        <div className="hidden sm:flex px-6 py-5 items-center gap-4">
          <div className="shrink-0 w-14 text-center">
            <p className="text-sm font-black text-foreground tracking-widest uppercase">{sessionDayLabel}</p>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="text-base font-bold text-foreground">{sessionName}</h3>
              {statusBadge(session.status, t)}
              {isJoined && (
                <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">
                  <CheckCircle2 size={10} />{t('Joined', '已加入')}
                </span>
              )}
              {isWaitlisted && (
                <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                  <AlertTriangle size={10} />{t('Waitlist', '候补')}
                </span>
              )}
              {isCancelled && (
                <span className="text-xs text-muted-foreground line-through">{t('Session cancelled', '训练已取消')}</span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Clock size={11} />{session.time}</span>
              {sessionLocation && <span className="flex items-center gap-1"><MapPin size={11} />{sessionLocation.split(',')[0]}</span>}
              {session.distance && <span className="flex items-center gap-1"><Gauge size={11} />{session.distance}</span>}
              {session.duration && <span className="flex items-center gap-1"><Clock size={11} />{session.duration}</span>}
              <span className="flex items-center gap-1">
                <Users size={11} />
                {participantCount}/{session.capacity} {t('joined', '已加入')}
                {waitlistCount > 0 && <span className="text-amber-600 ml-1">· {waitlistCount} {t('waitlist', '候补')}</span>}
              </span>
            </div>
          </div>
          <div className="shrink-0 flex items-center gap-3">
            <span className={`text-card-label px-2.5 py-1 rounded-full ${intensityBadgeClass(session.intensity)}`}>
              {intensityLabel(session.intensity, t)}
            </span>
            <ChevronDown
              size={18}
              className={`text-muted-foreground transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
      </button>

      {/* ── Expanded content ── */}
      <div
        className={`overflow-hidden transition-all duration-350 ${isExpanded ? 'max-h-[1400px] opacity-100' : 'max-h-0 opacity-0'}`}
        style={{ transitionProperty: 'max-height, opacity' }}
      >
        <div className="px-4 sm:px-6 pb-6 border-t border-border">
          {session.rotatingWeeks && (
            <div className="mt-5 mb-4">
              <div className="flex gap-1 flex-wrap mb-4">
                {session.rotatingWeeks.map((week, idx) => (
                  <button
                    key={`rotating-tab-${session.id}-${week.weekLabel}`}
                    onClick={() => setActiveRotatingWeek(idx)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 ${
                      activeRotatingWeek === idx
                        ? 'bg-foreground text-primary-foreground'
                        : 'bg-secondary text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t(week.weekLabel, week.weekLabelZH)}
                  </button>
                ))}
              </div>
              <RotatingWeekCard week={session.rotatingWeeks[activeRotatingWeek]} t={t} />
            </div>
          )}

          {!session.rotatingWeeks && (
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-3">
                <p className="text-card-label text-muted-foreground mb-1">{t('SESSION DETAILS', '训练详情')}</p>
                <div className="flex flex-col gap-2.5">
                  <div className="flex items-start gap-2 text-sm">
                    <Clock size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">{t('Time', '时间')}</p>
                      <p className="font-medium text-foreground">{session.time}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">{t('Location', '地点')}</p>
                      <p className="font-medium text-foreground">{sessionLocation}</p>
                      {sessionMeetingPoint && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {t('Meet:', '集合：')} {sessionMeetingPoint}
                        </p>
                      )}
                    </div>
                  </div>
                  {session.distance && (
                    <div className="flex items-start gap-2 text-sm">
                      <Gauge size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">{t('Distance', '距离')}</p>
                        <p className="font-medium text-foreground">{session.distance}</p>
                      </div>
                    </div>
                  )}
                  {session.duration && (
                    <div className="flex items-start gap-2 text-sm">
                      <Clock size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">{t('Duration', '时长')}</p>
                        <p className="font-medium text-foreground">{session.duration}</p>
                      </div>
                    </div>
                  )}
                  {session.pace && (
                    <div className="flex items-start gap-2 text-sm">
                      <Navigation size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">{t('Suggested Pace', '建议配速')}</p>
                        <p className="font-medium text-foreground tabular-nums">{session.pace}</p>
                      </div>
                    </div>
                  )}
                  {session.hrZone && (
                    <div className="flex items-start gap-2 text-sm">
                      <Heart size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">{t('Heart Rate Zone', '心率区间')}</p>
                        <p className="font-medium text-foreground">{session.hrZone}</p>
                      </div>
                    </div>
                  )}
                  {session.host && (
                    <div className="flex items-start gap-2 text-sm">
                      <User size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">{t('Host', '领跑')}</p>
                        <p className="font-medium text-foreground">{session.host}</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {session.tags.map((tag) => (
                    <span key={`tag-${session.id}-${tag}`} className="tag-chip">{tag}</span>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-4">
                {sessionGoal && (
                  <div>
                    <p className="text-card-label text-muted-foreground mb-2">{t('TRAINING GOAL', '训练目标')}</p>
                    <div className="flex items-start gap-2">
                      <Target size={15} className="text-accent mt-0.5 shrink-0" />
                      <p className="text-sm text-foreground leading-relaxed">{sessionGoal}</p>
                    </div>
                  </div>
                )}
                {sessionNotes && (
                  <div>
                    <p className="text-card-label text-muted-foreground mb-2">{t('NOTES', '备注')}</p>
                    <div className="bg-secondary/50 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground leading-relaxed italic">{sessionNotes}</p>
                    </div>
                  </div>
                )}
                {sessionSuitableFor && (
                  <div>
                    <p className="text-card-label text-muted-foreground mb-1">{t('SUITABLE FOR', '适合')}</p>
                    <p className="text-sm text-foreground flex items-center gap-1.5">
                      <Users size={13} className="text-muted-foreground" />
                      {sessionSuitableFor}
                    </p>
                  </div>
                )}

                {/* Participants + progress bar */}
                <div className="mt-auto pt-4 border-t border-border">
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-xs font-semibold text-foreground">
                        {participantCount}/{session.capacity} {t('joined', '已加入')}
                      </p>
                      <p className={`text-xs font-medium ${isFull ? 'text-red-500' : 'text-muted-foreground'}`}>
                        {isFull ? t('Full', '已满') : `${spotsLeft} ${t('spots left', '名额剩余')}`}
                      </p>
                    </div>
                    {/* Progress bar */}
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${isFull ? 'bg-red-400' : isJoined ? 'bg-green-500' : 'bg-accent'}`}
                        style={{ width: `${fillPct}%` }}
                      />
                    </div>
                    {waitlistCount > 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        {t('Waitlist', '候补')}: {waitlistCount}
                      </p>
                    )}
                  </div>

                  {isCancelled ? (
                    <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 rounded-lg px-3 py-2.5">
                      <XCircle size={15} />
                      {t('This session has been cancelled', '此训练已取消')}
                    </div>
                  ) : isJoined ? (
                    <button
                      onClick={onLeave}
                      disabled={joining}
                      className="w-full justify-center py-3 rounded-xl text-sm font-bold transition-all duration-150 flex items-center gap-2 bg-green-50 text-green-700 border-2 border-green-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                    >
                      {joining ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                      {t('✓ Joined — Tap to Leave', '已加入 — 点击退出')}
                    </button>
                  ) : isWaitlisted ? (
                    <button
                      onClick={onLeave}
                      disabled={joining}
                      className="w-full justify-center py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 flex items-center gap-2 bg-amber-50 text-amber-700 border border-amber-300 hover:bg-amber-100"
                    >
                      {joining ? <Loader2 size={15} className="animate-spin" /> : <AlertTriangle size={15} />}
                      {t('On Waitlist — Tap to Leave', '候补中 — 点击退出')}
                    </button>
                  ) : isFull ? (
                    <button
                      onClick={onJoin}
                      disabled={joining}
                      className="w-full justify-center py-3 rounded-xl text-sm font-bold transition-all duration-150 flex items-center gap-2 bg-amber-600 text-white hover:bg-amber-700"
                    >
                      {joining ? <Loader2 size={15} className="animate-spin" /> : <Users size={15} />}
                      {t('Join Waitlist', '加入候补')}
                    </button>
                  ) : (
                    <button
                      onClick={onJoin}
                      disabled={joining}
                      className="w-full justify-center py-3 rounded-xl text-sm font-bold transition-all duration-150 flex items-center gap-2 btn-primary"
                    >
                      {joining ? <Loader2 size={15} className="animate-spin" /> : <Users size={15} />}
                      {t('Join Session', '加入训练')}
                    </button>
                  )}

                  {session.routeLink && (
                    <a
                      href={session.routeLink}
                      className="mt-2 flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <MapPin size={12} />
                      {t('View Route Map', '查看路线地图')}
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {session.rotatingWeeks && (
            <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">
                  {t('Participants', '参与者')} ({participantCount}/{session.capacity})
                </p>
              </div>
              {!isCancelled && (
                isJoined ? (
                  <button
                    onClick={onLeave}
                    disabled={joining}
                    className="px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-150 flex items-center gap-2 bg-green-50 text-green-700 border-2 border-green-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                  >
                    {joining ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                    {t('✓ Joined — Leave', '已加入 — 退出')}
                  </button>
                ) : (
                  <button
                    onClick={onJoin}
                    disabled={joining}
                    className="px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-150 flex items-center gap-2 btn-primary"
                  >
                    {joining ? <Loader2 size={15} className="animate-spin" /> : null}
                    {isFull ? t('Join Waitlist', '加入候补') : t('Join Session', '加入训练')}
                  </button>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function WeeklyPlanClient() {
  const { t } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [pendingJoinId, setPendingJoinId] = useState<string | null>(null);
  const [joiningId, setJoiningId] = useState<string | null>(null);

  // Live data state — no mock fallback
  const [liveSessions, setLiveSessions] = useState<Session[]>([]);
  const [participantCounts, setParticipantCounts] = useState<Record<string, number>>({});
  const [waitlistCounts, setWaitlistCounts] = useState<Record<string, number>>({});
  const [userStatuses, setUserStatuses] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  // Guards: one load per mount, no parallel calls, no state after unmount
  const hasLoadedRef = useRef(false);
  const requestInFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const authResolvedRef = useRef(false);
  // Auto-retry: fires ONCE automatically when first load times out
  const autoRetryDoneRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    // Reset load guards on every mount — ensures fresh load on every page visit
    hasLoadedRef.current = false;
    requestInFlightRef.current = false;
    authResolvedRef.current = false;
    autoRetryDoneRef.current = false;
    setTimedOut(false);
    setError(null);
    // Invalidate cache on mount so route transitions always get fresh data
    invalidateCache('fetchWeeklySessions');
    return () => {
      mountedRef.current = false;
      hasLoadedRef.current = false;
      requestInFlightRef.current = false;
    };
  }, []);

  const loadSessions = useCallback(async () => {
    if (requestInFlightRef.current) {
      console.log('[WeeklyPlan] load already in flight, skipping');
      return;
    }
    requestInFlightRef.current = true;
    if (mountedRef.current) {
      setLoading(true);
      setError(null);
      setTimedOut(false);
    }

    console.log('[WeeklyPlan] load start');

    try {
      // Race: fetchWeeklySessions vs hard timeout
      // After timeout, show fallback UI — never infinite spinner
      const result = await Promise.race<LiveSession[] | 'timeout'>([
        fetchWeeklySessions(),
        new Promise<'timeout'>((resolve) =>
          setTimeout(() => resolve('timeout'), LOAD_TIMEOUT_MS)
        ),
      ]);

      if (!mountedRef.current) return;

      if (result === 'timeout') {
        console.warn('[WeeklyPlan] load timed out after', LOAD_TIMEOUT_MS / 1000, 's');
        requestInFlightRef.current = false;
        setLoading(false);
        setTimedOut(true);
        setError('Loading timed out. Please retry.');
        return;
      }

      const data = result as LiveSession[];
      const sessions = data.map(liveToSession);
      setLiveSessions(sessions);
      const counts: Record<string, number> = {};
      const waitlists: Record<string, number> = {};
      data.forEach((s) => {
        counts[s.id] = s.participants_count || 0;
        waitlists[s.id] = 0;
      });
      setParticipantCounts(counts);
      setWaitlistCounts(waitlists);
      if (sessions.length > 0) {
        setExpandedDay(sessions[0].id);
      }
      console.log('[WeeklyPlan] load success, count:', sessions.length);
    } catch (err: any) {
      if (!mountedRef.current) return;
      const msg: string = err?.message || '';
      const isExtensionError = msg.includes('content.js') || msg.includes('classList') || msg.includes('extension') || msg.includes('chrome-extension') || msg.includes('moz-extension');
      if (!isExtensionError) {
        console.warn('[WeeklyPlan] load failed:', msg);
        setError('Unable to load weekly plan. Please retry.');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        requestInFlightRef.current = false;
      }
    }
  }, []);

  const loadUserStatuses = useCallback(async () => {
    if (!user) { if (mountedRef.current) setUserStatuses({}); return; }
    try {
      const statuses = await getUserAllSessionStatuses();
      if (mountedRef.current) setUserStatuses(statuses);
    } catch {
      if (mountedRef.current) setUserStatuses({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (authLoading) return;
    authResolvedRef.current = true;
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    loadSessions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  // Auto-retry ONCE when first load times out — avoids requiring a hard reload
  // Guard: only fires once per mount, never stacks
  useEffect(() => {
    if (!timedOut || autoRetryDoneRef.current) return;
    autoRetryDoneRef.current = true;
    console.log('[WeeklyPlan] auto-retrying after timeout (once)');
    // Small delay before retry to let network recover
    const retryTimer = setTimeout(() => {
      if (!mountedRef.current) return;
      requestInFlightRef.current = false;
      setTimedOut(false);
      setError(null);
      invalidateCache('fetchWeeklySessions');
      loadSessions();
    }, 1500);
    return () => clearTimeout(retryTimer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timedOut]);

  useEffect(() => {
    if (!authResolvedRef.current) return;
    loadUserStatuses();
  }, [loadUserStatuses]);

  const handleToggle = (id: string) => {
    setExpandedDay((prev) => (prev === id ? null : id));
  };

  const handleJoin = async (sessionId: string) => {
    if (!user) {
      setPendingJoinId(sessionId);
      setLoginOpen(true);
      return;
    }
    setJoiningId(sessionId);
    try {
      const result = await joinSession(sessionId);
      if (result.success) {
        setUserStatuses((prev) => ({ ...prev, [sessionId]: result.status || 'joined' }));
        if (result.status === 'joined') {
          setParticipantCounts((prev) => ({ ...prev, [sessionId]: (prev[sessionId] || 0) + 1 }));
        } else if (result.status === 'waitlist') {
          setWaitlistCounts((prev) => ({ ...prev, [sessionId]: (prev[sessionId] || 0) + 1 }));
        }
      }
    } finally {
      setJoiningId(null);
    }
  };

  const handleLeave = async (sessionId: string) => {
    if (!user) return;
    setJoiningId(sessionId);
    try {
      const prevStatus = userStatuses[sessionId];
      const result = await leaveSession(sessionId);
      if (result.success) {
        setUserStatuses((prev) => {
          const next = { ...prev };
          delete next[sessionId];
          return next;
        });
        if (prevStatus === 'joined' || prevStatus === 'present' || prevStatus === 'late') {
          setParticipantCounts((prev) => ({ ...prev, [sessionId]: Math.max(0, (prev[sessionId] || 1) - 1) }));
        } else if (prevStatus === 'waitlist') {
          setWaitlistCounts((prev) => ({ ...prev, [sessionId]: Math.max(0, (prev[sessionId] || 1) - 1) }));
        }
      }
    } finally {
      setJoiningId(null);
    }
  };

  const handleLoginSuccess = () => {
    setLoginOpen(false);
    if (pendingJoinId) {
      const id = pendingJoinId;
      setPendingJoinId(null);
      setTimeout(() => handleJoin(id), 300);
    }
  };

  const handleRetry = () => {
    // Full reset — force fresh fetch, clear all retry guards
    requestInFlightRef.current = false;
    hasLoadedRef.current = false;
    autoRetryDoneRef.current = false;
    setTimedOut(false);
    setError(null);
    invalidateCache('fetchWeeklySessions');
    loadSessions();
  };

  const totalJoined = Object.values(userStatuses).filter((s) => s === 'joined' || s === 'present').length;

  // Sort sessions MON–SUN, then by time ascending within same day
  const DAY_SORT_ORDER: Record<string, number> = {
    Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3, Friday: 4, Saturday: 5, Sunday: 6,
  };

  function parseTimeMinutes(time: string): number {
    if (!time) return 9999;
    const match = time.match(/(\d{1,2}):(\d{2})/);
    if (!match) return 9999; // Flexible/Custom → last
    return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
  }

  const sortedSessions = [...liveSessions].sort((a, b) => {
    const dayA = DAY_SORT_ORDER[a.day] ?? 7;
    const dayB = DAY_SORT_ORDER[b.day] ?? 7;
    if (dayA !== dayB) return dayA - dayB;
    return parseTimeMinutes(a.time) - parseTimeMinutes(b.time);
  });

  return (
    <div className="min-h-screen bg-background">
      <Header isLoggedIn={!!user} onLoginClick={() => setLoginOpen(true)} />

      <main className="pt-16">
        {/* Compact hero */}
        <div style={{ backgroundColor: '#070707', borderBottom: '1px solid #111111' }}>
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-8 sm:py-10">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12, backgroundColor: '#B8FF00', padding: '3px 10px', borderRadius: 2 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: '#070707', textTransform: 'uppercase', fontFamily: 'var(--font-mono, monospace)' }}>
                {t('Weekly Training', '本周训练')}
              </span>
            </div>
            <h1 style={{ fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', fontWeight: 900, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', color: '#FFFFFF', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '-0.02em', lineHeight: 0.95 }}>
              {t('Weekly Training Plan', '每周训练计划')}
            </h1>
            <p style={{ color: 'rgba(247,247,242,0.55)', fontSize: 13, maxWidth: 480, fontFamily: 'var(--font-body, sans-serif)', lineHeight: 1.65 }}>
              {t(
                'Seven days of structured movement. Easy when it should be easy. Hard when it should be hard.',
                '七天有结构的运动。该轻松时轻松，该努力时努力。'
              )}
            </p>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 py-8 sm:py-10">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 size={24} className="animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t('Loading sessions…', '加载训练中…')}</p>
            </div>
          ) : (timedOut || error) ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-1">
                <AlertTriangle size={24} className="text-amber-500" />
              </div>
              <p className="text-base font-semibold text-foreground">
                {timedOut ? t('Loading timed out. Please retry.', '加载超时，请重试。') : error}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleRetry}
                  className="btn-primary text-sm flex items-center gap-2"
                >
                  <RefreshCw size={14} />
                  {t('Retry', '重试')}
                </button>
                <button
                  onClick={() => { setError(null); setTimedOut(false); }}
                  className="btn-secondary text-sm"
                >
                  {t('Continue', '继续')}
                </button>
              </div>
            </div>
          ) : liveSessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-2">
                <Users size={28} className="text-muted-foreground opacity-40" />
              </div>
              <p className="text-base font-semibold text-foreground">{t('This week has not been published yet.', '本周计划尚未发布。')}</p>
              <p className="text-sm text-muted-foreground">{t('Check back soon or contact your coach.', '请稍后查看或联系教练。')}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-w-4xl">
              {/* Your Joined Sessions — shown before full list when user is logged in */}
              {user && (
                <JoinedSessionsSummary
                  sessions={sortedSessions}
                  userStatuses={userStatuses}
                  participantCounts={participantCounts}
                  onLeave={handleLeave}
                  leavingId={joiningId}
                  t={t}
                />
              )}

              {/* Full session list — sorted MON–SUN */}
              {sortedSessions.map((session) => (
                <SessionAccordion
                  key={session.id}
                  session={session}
                  isExpanded={expandedDay === session.id}
                  onToggle={() => handleToggle(session.id)}
                  participationStatus={userStatuses[session.id] || null}
                  onJoin={() => handleJoin(session.id)}
                  onLeave={() => handleLeave(session.id)}
                  participantCount={participantCounts[session.id] ?? session.participants}
                  waitlistCount={waitlistCounts[session.id] ?? 0}
                  joining={joiningId === session.id}
                />
              ))}
            </div>
          )}

          <div className="mt-10 max-w-4xl grid grid-cols-2 gap-4">
            {[
              { id: 'summary-sessions', labelEN: 'Sessions This Week', labelZH: '本周训练次数', value: String(liveSessions.length) },
              { id: 'summary-joined', labelEN: 'Sessions Joined This Week', labelZH: '本周已加入', value: String(totalJoined) },
            ].map((stat) => (
              <div key={stat.id} className="bg-card rounded-xl border border-border p-4 shadow-card">
                <p className="text-card-label text-muted-foreground mb-1">{t(stat.labelEN, stat.labelZH)}</p>
                <p className="text-2xl font-bold text-foreground tabular-nums">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
      <LoginModal
        isOpen={loginOpen}
        onClose={() => { setLoginOpen(false); setPendingJoinId(null); }}
        onSuccess={handleLoginSuccess}
        promptMessage={pendingJoinId ? t(
          'Please log in or register before joining a session.',
          '请先登录或注册账户，再加入训练。'
        ) : undefined}
      />
    </div>
  );
}